import React, { useState, useRef } from 'react';
import { Upload, X, Image, Film } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FileUploader = ({ type = 'image', currentUrl = '', onUploadSuccess, label }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    // Validate file type
    const validTypes = type === 'image' 
      ? ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']
      : ['video/mp4', 'video/mpeg', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
    
    if (!validTypes.includes(file.type)) {
      alert(`Invalid file type. Please upload a valid ${type}.`);
      return;
    }

    // Validate file size
    const maxSize = type === 'image' ? 5 * 1024 * 1024 : 100 * 1024 * 1024; // 5MB for images, 100MB for videos
    if (file.size > maxSize) {
      alert(`File too large. Maximum size is ${type === 'image' ? '5MB' : '100MB'}.`);
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const endpoint = type === 'image' ? '/api/upload/image' : '/api/upload/video';
      const response = await axios.post(`${BACKEND_URL}${endpoint}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        const fileUrl = response.data.url;  // Use relative path directly
        setPreview(fileUrl);
        onUploadSuccess(fileUrl);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Error uploading file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setPreview('');
    onUploadSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      {label && <label className="block text-sm font-semibold mb-1">{label}</label>}
      
      {/* Preview */}
      {preview && (
        <div className="relative bg-slate-50 rounded-lg overflow-hidden">
          {type === 'image' ? (
            <div className="relative w-full" style={{ minHeight: '200px', maxHeight: '400px' }}>
              <img 
                src={preview} 
                alt="Preview" 
                className="w-full h-auto max-h-96 object-contain rounded-lg mx-auto block"
                style={{ maxWidth: '100%', height: 'auto' }}
              />
            </div>
          ) : (
            <div className="bg-slate-100 p-4 rounded-lg flex items-center gap-3">
              <Film size={32} className="text-cyan-500" />
              <div className="flex-1">
                <p className="font-semibold text-sm">Video uploaded</p>
                <p className="text-xs text-slate-500 break-all">{preview}</p>
              </div>
            </div>
          )}
          <button
            type="button"
            onClick={clearFile}
            className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors z-10"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Drag and Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragActive ? 'border-cyan-500 bg-cyan-50' : 'border-slate-300 bg-slate-50'
        } ${uploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-cyan-400'}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={type === 'image' ? 'image/*' : 'video/*'}
          onChange={handleChange}
          className="hidden"
          disabled={uploading}
        />

        <div className="flex flex-col items-center gap-2">
          {type === 'image' ? (
            <Image size={40} className="text-slate-400" />
          ) : (
            <Film size={40} className="text-slate-400" />
          )}
          
          {uploading ? (
            <div>
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-cyan-500 border-t-transparent mb-2"></div>
              <p className="text-sm text-slate-600">Uploading...</p>
            </div>
          ) : (
            <>
              <p className="text-sm font-semibold text-slate-700">
                Drag & drop {type} here, or click to browse
              </p>
              <p className="text-xs text-slate-500">
                {type === 'image' 
                  ? 'Supports: JPG, PNG, WebP, GIF (Max 5MB)'
                  : 'Supports: MP4, MOV, AVI, WebM (Max 100MB)'}
              </p>
            </>
          )}
        </div>
      </div>

      {/* Alternative: Manual URL Input */}
      {!preview && !uploading && (
        <div className="text-center text-xs text-slate-500">
          or paste URL below
        </div>
      )}
    </div>
  );
};

export default FileUploader;
